import { classNames, localeMoment } from "../../helpers";
import SessionModel from "../../api/models/SessionModel";
import { ClockIcon, CalendarIcon, PlusSmIcon } from "@heroicons/react/solid";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { getSessionState } from "./helper";
import { LinkIcon } from "@heroicons/react/outline";
import { TutorNoteInfoTable } from "./NoteInfoTable";
import { useState } from "react";
import NoteModel from "../../api/models/NoteModel";
import moment, { Moment } from "moment-timezone";
import AddNoteSide from "../modal/views/AddNoteSide";
import UserProfilePictureAvatar from "../avatar/UserProfilePictureAvatar";
import { isValidMoment } from "../../helpers/date_helpers";

type SessionInfoProps = {
  session: SessionModel;
  getSession?: any;
  sessionNumber?: number;
};
export default function TutorSessionInfo({
  session,
  getSession,
  sessionNumber,
}: SessionInfoProps) {
  const LocalBasePath = "page.tutor.dashboard.sessionInfo";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [addNote, setAddNote] = useState(false);
  const [showNotes, setShowNotes] =
    useState<null | "passed" | "unpassed">(null);
  const getNotesByState = (state: string | null, notes: NoteModel[]) => {
    if (!!state && session.notes!.length > 0) {
      return session.notes?.filter((note) => note.status === state);
    } else {
      return notes;
    }
  };
  const getDuration = (start: Moment, end: Moment) => {
    const d = end?.diff(start, "minutes");
    return moment.duration(d, "minutes").humanize();
  };
  return (
    <div
      className={classNames("bg-white")}
      data-cy="sessionInfo"
      data-cy-session-id={session.id}
    >
      <div className="relative pb-5  items-center space-x-3">
        <div className="py-6 px-6  bg-gray-50 border-b border-gray-200">
          <div className="flex-shrink-0 lg:flex block items-center justify-between">
            <div className="">
              <div className="flex-shrink-0 pt-2 flex items-center justify-between ">
                <h1 className={classNames("font-bold text-2xl")}>
                  {t("session")} #{sessionNumber || session.sessionNumber}
                </h1>
                <div className="flex-shrink-0 flex">
                  {getSessionState(session.status!)}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div
                  className={classNames(
                    "py-2 flex  items-center justify-between text-gray-400 "
                  )}
                >
                  <div className="  flex rounded-full items-center justify-between">
                    <CalendarIcon className="w-[18px]  rtl:ml-1 ltr:mr-1" />

                    {localeMoment(session!.date!).format("dddd L")}
                  </div>

                  <div
                    className={classNames(
                      " p-1 px-2.5 flex rounded-full items-center justify-between",
                      !isValidMoment(session.start_time) && "invisible"
                    )}
                  >
                    <ClockIcon className="w-[18px] rtl:ml-1 ltr:mr-1" />
                    {session.start_time
                      ?.locale(i18next.language)
                      .format("hh:mm A")}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              {session?.recordingUrl != null && (
                <a
                  target="_blank"
                  href={session?.recordingUrl}
                  className="inline-flex cursor-pointer items-center px-4 py-2   shadow-sm text-base font-medium rounded-md text-white bg-brandblue-600  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
                >
                  {t("showRedord")}
                </a>
              )}
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <UserProfilePictureAvatar
              profilePictureUrl={session.student?.profile_picture_url}
              className="w-6 h-6"
              alt="sand-user-img"
            />

            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-700">
                {session.student?.fullName}
              </h3>
              <p className="text-xs font-normal text-gray-400">
                {session.student?.recitation}
              </p>
            </div>
          </div>
        </div>
        <div className="px-6">
          <div className="columns-1  md:columns-2">
            <div className="pt-10">
              <h6 className="text-gray-500 text-sm	">{t("sessionTime")}</h6>
              <h6 className="text-gray-700 pt-1 text-lg">
                {session.start_time?.format("hh:mm A")}
                {" - "}
                {isValidMoment(session.end_time!) &&
                  session.end_time?.format("hh:mm A")}
              </h6>
            </div>
            <div className="pt-10">
              <h6 className="text-gray-500">{t("pagesRead")}</h6>
              <h6 className="text-gray-700 pt-1 text-lg">
                <span>{`${session.startPageNumber || 0} - `}</span>
                <span>{session.endPageNumber || ""}</span>
              </h6>
            </div>
          </div>
          <div className="columns-1  md:columns-2">
            <div className="pt-10">
              <h6 className="text-gray-500 text-sm	">{t("sessionDuration")}</h6>
              <h6 className="text-gray-700 pt-1 text-lg">
                {isValidMoment(session.end_time!) &&
                  isValidMoment(session.start_time) &&
                  getDuration(session.start_time!, session.end_time!)}
              </h6>
            </div>
            <div className="pt-10">
              <h6 className="text-gray-500">{t("sessionLatency")}</h6>
              <h6 className="text-gray-700 pt-1 text-lg">
                {moment.duration(session.latency || 0, "seconds").humanize()}
              </h6>
            </div>
          </div>
        </div>
        {/* 
          // TODO: add notes
          //  TODO: The following should be moved to it's own component something like TutorNotesTableActionsHeader 
          */}
        <div className="ps-6 pe-3">
          <div>
            <div className="pt-10">
              <h6 className="text-gray-500 text-sm	">
                {t("observationsMadeDuringTheSession")}
              </h6>
            </div>
            <div className="columns-1 my-2">
              <div className="w-full">
                <div className="flex  justify-between  text-sm w-full">
                  <div className="flex items-center justify-between  w-[210px]">
                    <button
                      onClick={() => {
                        setShowNotes(null);
                      }}
                      className={classNames(
                        // showNotes != null && "text-brandblue-500",
                        "text-gray-500"
                      )}
                    >
                      {t("everyone")}
                    </button>
                    <button
                      onClick={() => {
                        setShowNotes("unpassed");
                      }}
                      data-cy="showUnpassedNotes"
                      className={classNames(
                        (showNotes === null || showNotes === "unpassed") &&
                          "text-brandblue-500",
                        "text-gray-500"
                      )}
                    >
                      {t("unpassed")}
                    </button>
                    <button
                      onClick={() => {
                        setShowNotes("passed");
                      }}
                      data-cy="showPassedNotes"
                      className={classNames(
                        (showNotes === null || showNotes === "passed") &&
                          "text-brandblue-500",
                        "text-gray-500"
                      )}
                    >
                      {t("passed")}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAddNote(true)}
                    data-cy="addNote"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-brandblue-700 bg-brandblue-100 hover:bg-brandblue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandblue-500"
                  >
                    <PlusSmIcon className="h-5 w-5" aria-hidden="true" />
                    <span className="hidden md:block">{t("addNote")}</span>
                  </button>
                </div>
              </div>
            </div>
            <TutorNoteInfoTable
              sessionId={session?.id}
              getSession={getSession}
              notes={getNotesByState(showNotes, session?.notes ?? [])!}
            />
          </div>
        </div>
      </div>
      <AddNoteSide
        getSession={getSession}
        open={addNote}
        setOpen={setAddNote}
        sessionId={session.id}
        sessionNumber={session.sessionNumber}
      />
    </div>
  );
}
